import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/**
 * Reusable modal dialog component with backdrop
 * Uses React Portal to render at document body level
 * @param isOpen - Controls the visibility of the modal
 * @param onClose - Callback function when modal is closed
 * @param title - The title displayed in the modal header
 * @param children - The content to display inside the modal
 * @returns The rendered modal component or null if closed
 */
function Modal({ isOpen, onClose, title, children }: ModalProps): JSX.Element | null {
  /**
   * Handles escape key press to close modal
   * Uses capture phase to ensure topmost modal handles it first
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        onClose();
      }
    };

    if (isOpen) {
      // Use capture: true so this fires before other listeners
      // The topmost modal (added last) will handle it first
      document.addEventListener('keydown', handleEscape, { capture: false });
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: false });
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  /**
   * Handles backdrop click to close modal
   */
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-content">
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default Modal;
